import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Star, Shield, Flame, TrendingDown, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import { featuresList, testimonials } from '../mock/quizData';
import { API, useAuth } from '../context/AuthContext';
import SiteFooter from '../components/SiteFooter';
import { useLang } from '../context/LanguageContext';

const PlanPage = ({ answers }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tr } = useLang();
  const [selectedPlan, setSelectedPlan] = useState('3m');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const plans = [
    { id: '1m', name: t('monthPlan1'), per: '₹10', total: '₹300', badge: null },
    { id: '3m', name: t('monthPlan3'), per: '₹22', total: '₹1,999', badge: t('mostPopular') },
    { id: '6m', name: t('monthPlan6'), per: '₹16', total: '₹2,999', badge: t('bestValue') }
  ];

  const currentWeight = parseFloat(answers?.measurements?.weight) || 180;
  const targetWeight = parseFloat(answers?.targetWeight ?? answers?.['target-weight']) || Math.max(120, currentWeight - 25);
  const unit = answers?.measurements?.weightUnit || 'lbs';
  const loss = Math.max(0, currentWeight - targetWeight);

  const handleGetPlan = async () => {
    setError('');
    if (!user) {
      try {
        sessionStorage.setItem('pending_quiz_answers', JSON.stringify(answers));
        sessionStorage.setItem('pending_plan_id', selectedPlan);
      } catch (_) {}
      navigate('/login?next=/plan');
      return;
    }

    setBusy(true);
    try {
      await axios.post(`${API}/quiz/save`, { answers }, { withCredentials: true });
      const orderRes = await axios.post(
        `${API}/payment/create-order`,
        { plan_id: selectedPlan },
        { withCredentials: true }
      );
      const order = orderRes.data;

      if (order.mock) {
        const verifyRes = await axios.post(
          `${API}/payment/verify`,
          {
            order_id: order.order_id,
            payment_id: `pay_mock_${Date.now()}`,
            signature: 'mock_signature',
            plan_id: selectedPlan,
          },
          { withCredentials: true }
        );
        setSuccess({
          plan: verifyRes.data.plan_name,
          expires: verifyRes.data.expires_at,
          mock: verifyRes.data.mock,
        });
        setBusy(false);
        return;
      }

      await loadRazorpay();
      if (!window.Razorpay) {
        throw new Error('Razorpay checkout could not be loaded. Please check your connection and try again.');
      }

      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Myhealthyfy',
        description: order.plan_name || 'Fitness plan subscription',
        order_id: order.order_id,
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: { color: '#ff4e15' },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API}/payment/verify`,
              {
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                plan_id: selectedPlan,
              },
              { withCredentials: true }
            );
            setSuccess({
              plan: verifyRes.data.plan_name,
              expires: verifyRes.data.expires_at,
              mock: verifyRes.data.mock,
            });
          } catch (err) {
            setError(err?.response?.data?.detail || 'Payment could not be verified. If money was deducted, please contact support.');
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.on('payment.failed', (resp) => {
        setError(`${t('paymentFailedPrefix')} ${resp?.error?.description || ''}`);
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Payment failed. Please try again.');
      setBusy(false);
    }
  };

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  if (success) {
    return (
      <div className="min-h-screen bg-[#0d0e10] text-white">
        <Header />
        <main className="px-4 py-16">
          <div className="max-w-md mx-auto text-center fade-in-up" data-testid="plan-success">
            <div className="w-20 h-20 rounded-full bg-[#ff4e15]/20 border-2 border-[#ff4e15] flex items-center justify-center mx-auto">
              <Check size={40} className="text-[#ff4e15]" />
            </div>
            <h1 className="heading-font text-4xl font-black mt-6 tracking-wide">{t('youreIn')}</h1>
            <p className="mt-3 text-white/80">{success.plan} {t('activated')}</p>
            <p className="mt-1 text-sm text-white/50">
              {t('validUntil')} {new Date(success.expires).toLocaleDateString()}
            </p>
            {success.mock && (
              <p className="mt-4 text-xs text-yellow-500/80 bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 rounded-sm">
                {t('paymentSimulated')}
              </p>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-8 w-full py-3.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] transition-colors"
              data-testid="go-dashboard-btn"
            >
              {t('goToDashboard')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 md:px-8 py-6 md:py-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center fade-in-up">
            <p className="text-[#ff4e15] font-bold text-sm tracking-widest">{t('yourPlanIsReady')}</p>
            <h1 className="heading-font text-4xl md:text-5xl font-black mt-2 tracking-wide">
              {t('personalWorkoutPlan')}<br />
              <span className="text-[#ff4e15]">{t('basedOnAnswers')}</span>
            </h1>
          </div>

          <div className="mt-8 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wide">{t('yourForecast')}</div>
                <div className="text-lg font-bold flex items-center gap-2 mt-1">
                  <TrendingDown size={20} className="text-[#ff4e15]" />
                  {t('reachBy')} {targetWeight} {unit} {t('byWeek12')}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-white/60">{t('totalLoss')}</div>
                <div className="text-2xl heading-font font-black text-[#ff4e15]">
                  {loss.toFixed(1)} {unit}
                </div>
              </div>
            </div>
            <div className="relative h-40">
              <svg viewBox="0 0 400 160" className="w-full h-full">
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff4e15" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#ff4e15" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M 10 30 Q 100 40 150 60 T 260 100 T 390 140 L 390 160 L 10 160 Z" fill="url(#g1)" />
                <path d="M 10 30 Q 100 40 150 60 T 260 100 T 390 140" fill="none" stroke="#ff4e15" strokeWidth="3" />
                <circle cx="10" cy="30" r="5" fill="#ff4e15" />
                <circle cx="390" cy="140" r="6" fill="#ff4e15" />
              </svg>
              <div className="absolute top-1 left-2 text-xs text-white/70">{currentWeight} {unit}</div>
              <div className="absolute bottom-1 right-2 text-xs text-white font-bold">{targetWeight} {unit}</div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-white/50">
              <span>{t('week')} 1</span><span>{t('week')} 4</span><span>{t('week')} 8</span><span>{t('week')} 12</span>
            </div>
          </div>

          {/* Features */}
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">{t('whatYoullGet')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {featuresList.map((f, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">
                  <span className="w-6 h-6 rounded-full bg-[#ff4e15]/20 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-[#ff4e15]" />
                  </span>
                  <span className="text-sm">{tr(f)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Plans */}
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">{t('chooseYourPlan')}</h2>
            <div className="space-y-3">
              {plans.map((p) => {
                const active = selectedPlan === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlan(p.id)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-sm border-2 text-left transition-all ${
                      active ? 'border-[#ff4e15] bg-[#1f1210]' : 'border-[#2a2b2f] bg-[#1a1b1e] hover:border-[#3a3b3f]'
                    }`}
                    data-testid={`plan-${p.id}-btn`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        active ? 'border-[#ff4e15]' : 'border-[#4a4b4f]'
                      }`}>
                        {active && <span className="w-2.5 h-2.5 rounded-full bg-[#ff4e15]" />}
                      </span>
                      <div>
                        <div className="font-bold flex items-center gap-2">
                          {p.name}
                          {p.badge && (
                            <span className="bg-[#ff4e15] text-white text-[10px] px-2 py-0.5 rounded-sm font-bold tracking-wide">
                              {p.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/60 mt-0.5">{t('total')}: {p.total}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="heading-font font-black text-2xl text-[#ff4e15]">{p.per}</div>
                      <div className="text-[10px] text-white/60">{t('perDay')}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="mt-4 flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handleGetPlan}
              disabled={busy}
              className="mt-6 w-full py-4 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 pulse-orange transition-colors flex items-center justify-center gap-2"
              data-testid="get-plan-btn"
            >
              <Flame size={18} />
              {busy ? t('processing') : user ? t('getMyPlan') : t('signInToContinue')}
            </button>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/60">
              <Shield size={14} />
              {t('moneyBack')}
            </div>
          </div>

          {/* Testimonials */}
          <div className="mt-12">
            <h2 className="text-xl font-bold mb-4">{t('peopleLove')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((tsn, i) => (
                <div key={i} className="p-5 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md">
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={14} className="text-[#ff4e15] fill-[#ff4e15]" />
                    ))}
                  </div>
                  <div className="text-sm text-white/80 italic">{tr(tsn.quote)}</div>
                  <div className="mt-3 pt-3 border-t border-[#2a2b2f]">
                    <div className="font-bold text-sm">{tr(tsn.name)}</div>
                    <div className="text-xs text-[#ff4e15] font-semibold">{tr(tsn.result)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-[#ff4e15] text-sm underline transition-colors">
              {t('retakeQuiz')}
            </button>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default PlanPage;
